import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Client } from "@xmpp/client"
import { JID } from "@xmpp/jid"
import { addDays, startOfDay } from "date-fns"
import i18n from "@/lib/i18n/i18n"
import AdHocCommands from "@/api/adHocCommands"
import { SearchResultParser, SearchHit } from "@/lib/searchResultParser"

// The streamed search command returns up to 50 hits; a full page means more may exist.
const SEARCH_RESULTS_PAGE_SIZE = 50

export default function useMessageSearch({
  client,
  myJid,
  isOpen,
  chatJid,
  searchQuery,
  dateRange,
}: {
  client: Client
  myJid: JID
  isOpen: boolean
  chatJid?: string
  searchQuery: string
  dateRange: { from: Date; to: Date } | null
}) {
  const [hits, setHits] = useState<SearchHit[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [hasMoreResults, setHasMoreResults] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string>()
  const requests = useRef({ generation: 0, loadingMore: false }).current
  const searchTimer = useRef<ReturnType<typeof setTimeout>>()
  const since = useMemo(
    () => (dateRange ? startOfDay(dateRange.from) : undefined),
    [dateRange],
  )
  const until = useMemo(
    () => (dateRange ? addDays(startOfDay(dateRange.to), 1) : undefined),
    [dateRange],
  )
  const search = useCallback(async () => {
    clearTimeout(searchTimer.current)
    if (!isOpen) return
    const currentGeneration = ++requests.generation
    requests.loadingMore = false
    setLoadingMore(false)
    setHits([])
    setCurrentPage(0)
    setHasMoreResults(false)
    setError(undefined)
    if (!searchQuery.trim()) {
      setLoading(false)
      return
    }
    setLoading(true)
    window.analytics.track("SearchDone", {
      userJid: myJid.toString(),
      query: searchQuery,
      chatJid,
    })
    try {
      const response = await AdHocCommands.searchStreamed({
        client,
        query: searchQuery,
        chatJid: chatJid || "*",
        page: 0,
        since,
        until,
      })
      if (requests.generation !== currentGeneration) return
      const results = SearchResultParser.addFileInfo(response.hits)
      setHits(results)
      setHasMoreResults(results.length === SEARCH_RESULTS_PAGE_SIZE)
    } catch (cause) {
      if (requests.generation !== currentGeneration) return
      console.error("Failed to search messages", cause)
      setError(i18n.t("app.search.errors.searchFailed"))
    } finally {
      if (requests.generation === currentGeneration) setLoading(false)
    }
  }, [client, searchQuery, myJid, chatJid, since, until, isOpen, requests])

  useEffect(() => {
    requests.generation++
    setHits([])
    setHasMoreResults(false)
    setLoading(isOpen && !!searchQuery.trim())
    const timer = setTimeout(() => void search(), 300)
    searchTimer.current = timer
    return () => {
      clearTimeout(timer)
      requests.generation++
    }
  }, [search, requests, isOpen, searchQuery])

  const loadMore = useCallback(async () => {
    if (!hasMoreResults || requests.loadingMore || loading) return
    requests.loadingMore = true
    const currentGeneration = requests.generation
    setLoadingMore(true)
    setError(undefined)
    try {
      const response = await AdHocCommands.searchStreamed({
        client,
        query: searchQuery,
        chatJid: chatJid || "*",
        page: currentPage + 1,
        since,
        until,
      })
      if (requests.generation !== currentGeneration) return
      const results = SearchResultParser.addFileInfo(response.hits)
      setHits((previous) => {
        const existingIds = new Set(previous.map((hit) => hit.document.id))
        return [
          ...previous,
          ...results.filter((hit) => !existingIds.has(hit.document.id)),
        ]
      })
      setCurrentPage((page) => page + 1)
      setHasMoreResults(results.length === SEARCH_RESULTS_PAGE_SIZE)
    } catch (cause) {
      if (requests.generation !== currentGeneration) return
      console.error("Failed to load search results", cause)
      setError(i18n.t("app.search.errors.loadMoreFailed"))
    } finally {
      if (requests.generation === currentGeneration) {
        requests.loadingMore = false
        setLoadingMore(false)
      }
    }
  }, [
    hasMoreResults,
    loading,
    client,
    searchQuery,
    chatJid,
    currentPage,
    since,
    until,
    requests,
  ])

  const cancel = useCallback(() => {
    requests.generation++
  }, [requests])
  return {
    hits,
    hasMoreResults,
    loading,
    loadingMore,
    error,
    search,
    loadMore,
    cancel,
  }
}
